import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Send, HelpCircle, Loader2, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/app/Header";
import Avatar from "../components/app/Avatar";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProChatLock, canSendMessages } from "../components/app/ProChatLock";

/**
 * ChatRoom — public #general channel where every signed-up camper can
 * post. Ticking a "help wanted" flag broadcasts a notification to every
 * other member so the community can jump in.
 *
 * Polls /chatroom/messages every 3s while the tab is visible. Marks
 * read on mount so the header badge clears immediately.
 */
export default function ChatRoom() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [helpWanted, setHelpWanted] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/chatroom/messages?limit=100");
      setMsgs(data.items || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 30);
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    api.post("/chatroom/mark-read").catch(() => {});
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 3000);
    return () => clearInterval(id);
  }, [user, load]);

  if (!user) {
    return (
      <>
        <Header />
        <main className="max-w-md mx-auto px-4 py-16 text-center">
          <Users className="w-10 h-10 mx-auto text-[#ff4500] mb-3" />
          <h1 className="font-display text-3xl mb-2">Sign in to join the chat</h1>
          <p className="text-sm text-[#666] mb-4">The community chat room is member-only.</p>
          <Link to="/login" className="btn-primary inline-block">Log in</Link>
        </main>
      </>
    );
  }

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post("/chatroom/send", { text: t, help_wanted: helpWanted });
      setText("");
      if (helpWanted) toast.success("Help-wanted flag sent — everyone gets a ping 🙌");
      setHelpWanted(false);
      await load();
      api.post("/chatroom/mark-read").catch(() => {});
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSending(false); }
  };

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-6" data-testid="chatroom-page">
        <div className="mb-4">
          <Link
            to="/messages"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#ff4500] hover:text-[#e63e00] mb-2"
            data-testid="chatroom-back-to-messages"
          >
            ← Back to Messages
          </Link>
          <h1 className="font-display text-3xl flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-[#ff4500]" /> Community chat
          </h1>
          <p className="text-xs text-[#666] mt-1">
            One shared room for every camper. Need advice fast? Tick <em>Help wanted</em> — everyone else gets a notification.
          </p>
        </div>

        <div className="border hairline bg-white flex flex-col h-[68vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#fafafa]">
            {msgs.length === 0 ? (
              <div className="text-center text-xs text-[#888] mt-8">No messages yet. Start the conversation 👋</div>
            ) : (
              msgs.map((m) => {
                const mine = m.sender_id === user.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"} items-end gap-2`} data-testid={`chatroom-msg-${m.message_id}`}>
                    {!mine && <Avatar src={m.sender_picture} name={m.sender_name} size={28} ringed={false} />}
                    <div className={`max-w-[75%] ${mine ? "text-right" : "text-left"}`}>
                      {!mine && (
                        <div className="text-[10px] mono uppercase tracking-wider text-[#666] mb-0.5 pl-2">{m.sender_name}</div>
                      )}
                      <div className={`inline-block px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#ff4500] text-white rounded-br-sm" : "bg-white border hairline rounded-bl-sm"}`}>
                        {m.help_wanted && (
                          <div className={`text-[10px] mono uppercase tracking-wider font-bold mb-1 ${mine ? "text-white/90" : "text-[#ff4500]"}`}>
                            🆘 Help wanted
                          </div>
                        )}
                        {m.text}
                        <div className={`text-[9px] mt-1 opacity-70 ${mine ? "text-white/80" : "text-[#666]"}`}>
                          {new Date(m.sent_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {!canSendMessages(user) ? (
            <ProChatLock what="post in the community chat" testId="chatroom-pro-lock" />
          ) : (
          <form onSubmit={send} className="border-t hairline bg-white p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 1500))}
                placeholder={helpWanted ? "Describe what you need help with…" : "Say something to the community…"}
                className="flex-1 border hairline px-3 py-2 text-sm focus:outline-none focus:border-[#ff4500]"
                data-testid="chatroom-input"
                maxLength={1500}
              />
              <button
                type="submit"
                disabled={sending || !text.trim()}
                className="btn-primary text-xs px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="chatroom-send"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
              </button>
            </div>
            <label className="flex items-center gap-2 mt-2 text-xs text-[#555] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={helpWanted}
                onChange={(e) => setHelpWanted(e.target.checked)}
                className="accent-[#ff4500]"
                data-testid="chatroom-help-toggle"
              />
              <HelpCircle className="w-3.5 h-3.5 text-[#ff4500]" />
              <span>Flag as <strong>Help wanted</strong> — everyone gets a notification</span>
            </label>
          </form>
          )}
        </div>
      </main>
    </>
  );
}
